import { useEffect, useState } from 'react';
import { Bell, Check, Pencil, X } from 'lucide-react';
import { Drawer } from 'vaul';
import { fetchAccountEntitlements } from '../lib/api/account';
import { createAlarm, deleteAlarm, fetchAlarms } from '../lib/api/alarms';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { AccountEntitlementsResponse, ChartSymbol, PriceAlarm } from '../types/api';
import { AlarmPriceChart } from './AlarmPriceChart';
import { alarmTimeframes } from './alarmTimeframes';
import { useAlarmSetup } from './useAlarmSetup';

interface AlarmSetupSheetProps {
  open: boolean;
  symbol: ChartSymbol | null;
  initialPrice?: number;
  onOpenChange(open: boolean): void;
}

export function AlarmSetupSheet({ open, symbol, initialPrice, onOpenChange }: AlarmSetupSheetProps) {
  const setup = useAlarmSetup({ symbol, initialPrice });
  const push = usePushNotifications();
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dismissedError, setDismissedError] = useState('');
  const [alarms, setAlarms] = useState<PriceAlarm[]>([]);
  const [deletingAlarmId, setDeletingAlarmId] = useState<string | null>(null);
  const [entitlements, setEntitlements] = useState<AccountEntitlementsResponse | null>(null);

  useEffect(() => {
    if (!open) {
      setEditingPrice(false);
      setSaveError('');
      setDismissedError('');
      setDeletingAlarmId(null);
      setEntitlements(null);
      return;
    }
    void refreshEntitlements();
  }, [open]);

  useEffect(() => {
    if (editingPrice) {
      setPriceInput(setup.targetPrice ? formatPrice(setup.targetPrice, symbol?.pricescale, true) : '');
    } else {
      setPriceInput(setup.targetPrice ? formatPrice(setup.targetPrice, symbol?.pricescale) : '');
    }
  }, [editingPrice, setup.targetPrice, symbol?.pricescale]);

  useEffect(() => {
    if (!open || !symbol) {
      setAlarms([]);
      return;
    }

    let cancelled = false;
    void fetchAlarms().then((nextAlarms) => {
      if (cancelled) return;
      setAlarms(nextAlarms.filter((alarm) => alarm.symbol === symbol.name && alarm.status === 'active'));
    }).catch(() => {
      if (!cancelled) setAlarms([]);
    });

    return () => {
      cancelled = true;
    };
  }, [open, symbol?.name]);

  function commitPriceInput() {
    const nextPrice = Number.parseFloat(priceInput);
    if (Number.isFinite(nextPrice) && nextPrice > 0) {
      setup.setTargetPrice(roundToPricescale(nextPrice, symbol?.pricescale));
      setEditingPrice(false);
    } else if (priceInput.trim() === '') {
      setEditingPrice(false);
    }
  }

  async function refreshEntitlements() {
    try {
      setEntitlements(await fetchAccountEntitlements());
    } catch {
      setEntitlements(null);
    }
  }

  async function saveAlarm() {
    if (!symbol || !setup.valid || setup.targetPrice === null) return;
    setSaving(true);
    setSaveError('');
    try {
      const alarm = await createAlarm({
        symbol: symbol.name,
        timeframe: setup.timeframe,
        target_price: setup.targetPrice,
        direction: setup.direction,
        trigger_mode: 'latest_quote',
        repeat_mode: 'once',
      });
      setAlarms((current) => [alarm, ...current.filter((item) => item.id !== alarm.id)]);
      await refreshEntitlements();
      onOpenChange(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save alarm');
    } finally {
      setSaving(false);
    }
  }

  async function removeAlarm(id: string) {
    setDeletingAlarmId(id);
    setSaveError('');
    try {
      await deleteAlarm(id);
      setAlarms((current) => current.filter((alarm) => alarm.id !== id));
      await refreshEntitlements();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to delete alarm');
    } finally {
      setDeletingAlarmId(null);
    }
  }

  const priceLabel = setup.targetPrice ? formatPrice(setup.targetPrice, symbol?.pricescale) : '--';
  const directionLabel = setup.direction === 'above' ? 'At or above' : 'At or below';
  const activeAlarmUsage = entitlements?.usage['alarm.active_count'] ?? alarms.length;
  const activeAlarmLimit = entitlements?.entitlements['alarm.active_limit']?.limit_value ?? null;
  const activeAlarmLimitReached = activeAlarmLimit !== null && activeAlarmUsage >= activeAlarmLimit;
  const pushDeviceUsage = entitlements?.usage['alarm.push_device_count'] ?? null;
  const pushDeviceLimit = entitlements?.entitlements['alarm.push_device_limit']?.limit_value ?? null;
  const pushDeviceLimitReached = push.state !== 'subscribed' && pushDeviceLimit !== null && pushDeviceUsage !== null && pushDeviceUsage >= pushDeviceLimit;
  const currentError = saveError || setup.error || (activeAlarmLimitReached ? 'Active alarm limit reached.' : '');
  const visibleError = currentError && currentError !== dismissedError ? currentError : '';

  useEffect(() => {
    if (!currentError) {
      setDismissedError('');
      return undefined;
    }

    setDismissedError('');
    const timeout = window.setTimeout(() => setDismissedError(currentError), 5000);
    return () => window.clearTimeout(timeout);
  }, [currentError]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="drawer-overlay alarm-setup-overlay" />
        <Drawer.Content className="alarm-setup-sheet" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
          <div className="alarm-setup-header">
            <button type="button" className="icon-button" aria-label="Cancel alarm" onClick={() => onOpenChange(false)}>
              <X size={24} strokeWidth={2} />
            </button>
            <div className="alarm-setup-title">
              <Drawer.Title asChild><strong>{symbol?.description || symbol?.name || 'Price alarm'}</strong></Drawer.Title>
              <span>{symbol ? `${symbol.name} · ${symbol.exchange}` : 'Select a symbol'}</span>
            </div>
            <button type="button" className="alarm-done-button" disabled={!setup.valid || saving || activeAlarmLimitReached} onClick={() => void saveAlarm()}>
              {saving ? 'Saving' : 'Done'}
            </button>
          </div>

          <div className="alarm-price-summary">
            <span>{directionLabel}</span>
            {editingPrice ? (
              <input
                className="alarm-price-input"
                inputMode="decimal"
                lang="en-US"
                value={priceInput}
                autoFocus
                onChange={(event) => setPriceInput(event.target.value.replace(/,/g, '.'))}
                onBlur={commitPriceInput}
                onKeyDown={(event) => {
                  if (event.key === ',') {
                    event.preventDefault();
                    if (!priceInput.includes('.')) {
                      setPriceInput((current) => current + '.');
                    }
                  }
                  if (event.key === 'Enter') commitPriceInput();
                  if (event.key === 'Escape') setEditingPrice(false);
                }}
              />
            ) : (
              <button type="button" className="alarm-price-edit-button" onClick={() => setEditingPrice(true)}>
                <strong>{priceLabel}</strong>
                <Pencil size={22} strokeWidth={2.3} aria-hidden="true" />
              </button>
            )}
            {setup.latestPrice !== null && <small>Latest {formatPrice(setup.latestPrice, symbol?.pricescale)}</small>}
          </div>

          <div className="alarm-chart-wrap">
            {symbol && (
              <AlarmPriceChart
                symbol={symbol.name}
                pricescale={symbol.pricescale}
                timeframe={setup.timeframe}
                targetPrice={setup.targetPrice}
                currentPrice={setup.latestPrice}
                alarms={alarms}
                deletingAlarmId={deletingAlarmId}
                onTargetPriceChange={setup.setTargetPrice}
                onEditPrice={() => setEditingPrice(true)}
                onDeleteAlarm={(id) => void removeAlarm(id)}
              />
            )}
          </div>

          <div className="alarm-timeframes" aria-label="Alarm chart timeframe">
            {alarmTimeframes.map((item) => (
              <button
                key={item.value}
                type="button"
                className={setup.timeframe === item.value ? 'active' : ''}
                onClick={() => setup.setTimeframe(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="alarm-push-card">
            <Bell size={22} strokeWidth={2} aria-hidden="true" />
            <div>
              <strong>{pushStatusTitle(push.state)}</strong>
              <span>{pushDeviceLimitReached ? pushLimitMessage(pushDeviceUsage, pushDeviceLimit) : pushStatusMessage(push.state, push.error, pushDeviceUsage, pushDeviceLimit)}</span>
            </div>
            {push.state !== 'subscribed' && push.state !== 'unsupported' && push.state !== 'install-required' && (
              <button type="button" onClick={() => void push.enable().then(refreshEntitlements)} disabled={push.state === 'subscribing' || pushDeviceLimitReached}>
                {push.state === 'subscribing' ? 'Enabling' : 'Enable'}
              </button>
            )}
            {push.state === 'subscribed' && <Check size={20} strokeWidth={2.2} aria-label="Notifications enabled" />}
          </div>

          {visibleError && (
            <div className="alarm-setup-error">
              <span>{visibleError}</span>
              <button type="button" aria-label="Dismiss error" onClick={() => setDismissedError(visibleError)}>
                <X size={16} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function formatPrice(price: number, pricescale = 100, raw = false): string {
  const decimals = priceDecimals(pricescale);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: raw ? 0 : decimals,
    maximumFractionDigits: decimals,
    useGrouping: !raw,
  }).format(price);
}

function roundToPricescale(price: number, pricescale = 100): number {
  const decimals = priceDecimals(pricescale);
  const factor = 10 ** decimals;
  return Math.round(price * factor) / factor;
}

function priceDecimals(pricescale: number): number {
  if (!Number.isFinite(pricescale) || pricescale <= 1) return 2;
  return Math.min(8, Math.max(0, Math.round(Math.log10(pricescale))));
}

function pushStatusTitle(state: string): string {
  switch (state) {
    case 'subscribed':
      return 'Notifications enabled';
    case 'install-required':
      return 'Install app first';
    case 'permission-denied':
      return 'Notifications blocked';
    case 'unsupported':
      return 'Push unavailable';
    case 'subscribing':
      return 'Enabling notifications';
    case 'error':
      return 'Notification setup failed';
    default:
      return 'Enable push notifications';
  }
}

function pushStatusMessage(state: string, error: string, usage: number | null, limit: number | null): string {
  const suffix = usage !== null && limit !== null ? ` ${usage}/${limit} devices used.` : '';
  switch (state) {
    case 'subscribed':
      return `Alarm will push to this device.${suffix}`;
    case 'install-required':
      return 'On iOS, add Chartify to Home Screen and open it there.';
    case 'permission-denied':
      return 'Allow notifications in browser or iOS settings.';
    case 'unsupported':
      return 'This browser does not support Web Push.';
    case 'subscribing':
      return 'Waiting for permission and device subscription.';
    case 'error':
      return error || 'Try enabling notifications again.';
    default:
      return `Needed to receive alarm pushes on this device.${suffix}`;
  }
}

function pushLimitMessage(usage: number | null, limit: number | null): string {
  if (usage === null || limit === null) return 'Push device limit reached.';
  return `${usage}/${limit} push devices used. Disable another device before adding this one.`;
}
